import { CATEGORIES_FICHIER, type CategorieFichier } from './stockage'
import { supabase } from './supabase'

/**
 * Lecture d'un dossier complet : l'entete, ses trois documents, son rapport.
 *
 * Un dossier archive est traite comme introuvable — le filtre est dans la
 * requete, pas dans l'affichage : une page qui recevrait la ligne et choisirait
 * de ne pas la montrer resterait lisible depuis la console.
 */

export interface FichierDossier {
  readonly categorie: CategorieFichier
  readonly nom_fichier: string
  readonly octets: number
  readonly type_mime: string
}

export interface DossierComplet {
  readonly id: string
  readonly nom: string
  readonly prenom: string
  readonly cree_le: string
  readonly homonyme: boolean
  /** Lien du CV en Google Docs, ecrit par la copie Drive (0008). Null tant qu'il n'y en a pas. */
  readonly lien_google_doc: string | null
  readonly fichiers: readonly FichierDossier[]
  readonly rapport: { readonly donnees: unknown; readonly version_format: string } | null
}

export class ErreurDossier extends Error {
  override readonly cause: unknown

  constructor(message: string, cause?: unknown) {
    super(message)
    this.name = 'ErreurDossier'
    this.cause = cause
  }
}

/** Rend `null` si le dossier n'existe pas, ou s'il est archive. */
export async function lireDossier(id: string): Promise<DossierComplet | null> {
  const { data, error } = await supabase
    .from('dossiers')
    .select(
      'id, nom, prenom, cree_le, homonyme, lien_google_doc, fichiers(categorie, nom_fichier, octets, type_mime), rapports(donnees, version_format)',
    )
    .eq('id', id)
    .is('archive_le', null)
    .maybeSingle()

  if (error) throw new ErreurDossier(error.message, error)
  if (data === null) return null

  const ligne = data as unknown as {
    id: string
    nom: string
    prenom: string
    cree_le: string
    homonyme: boolean | null
    lien_google_doc: string | null
    fichiers: FichierDossier[] | null
    rapports: { donnees: unknown; version_format: string }[] | { donnees: unknown; version_format: string } | null
  }

  // `rapports` arrive en tableau ou en objet selon que PostgREST detecte la
  // relation comme un-a-un : dossier_id y est unique (0001).
  const rapports = Array.isArray(ligne.rapports) ? ligne.rapports[0] ?? null : ligne.rapports

  return {
    id: ligne.id,
    nom: ligne.nom,
    prenom: ligne.prenom,
    cree_le: ligne.cree_le,
    homonyme: ligne.homonyme === true,
    lien_google_doc: ligne.lien_google_doc ?? null,
    fichiers: ordonner(ligne.fichiers ?? []),
    rapport: rapports ?? null,
  }
}

/**
 * Ordre d'affichage fixe : CV d'origine, PDF, Word.
 *
 * L'ordre de la base n'est pas garanti, et un conseiller qui ouvre trois
 * dossiers d'affilee doit trouver le meme document a la meme place.
 */
function ordonner(fichiers: readonly FichierDossier[]): FichierDossier[] {
  const rang = new Map<CategorieFichier, number>(
    CATEGORIES_FICHIER.map((categorie, index) => [categorie, index]),
  )
  return [...fichiers].sort(
    (a, b) => (rang.get(a.categorie) ?? 99) - (rang.get(b.categorie) ?? 99),
  )
}

/** Libelles affiches, dans l'ordre du tableau. */
export const LIBELLE_CATEGORIE: Record<CategorieFichier, string> = {
  cv_origine: "CV d'origine",
  cv_pdf: 'CV refondu — PDF',
  cv_docx: 'CV refondu — Word',
}
